// The dice bag's HTTP surface as the client uses it (slice 11): the person's
// own account-wide bag under `/api/me/dice`, a page of the table's log as
// this person may see it, and the DM's saved file. Rolls themselves go over
// the socket as `roll.request` and come back as events; nothing here throws.
import { parsePageHandouts, type Handouts } from './handouts';
import { parsePageDecks, type Decks } from './cards';
import { defaultBag, normaliseRulings, parseBag, parsePlayerRulings, parseRules, type Bag, type PlayerRuling, type Ruling, type PlayerAsk, type PlayerAttack, type PlayerSave, type PlayerTrigger, type RollAsk, type RollRecord, type TableRules } from '@dragons-table/vtt-shared';
import { requestJson } from './api';
import { authFetch } from './session';

export type LogEntry =
  /** `rewound` (issue 1169): made in minutes the DM later rewound — it stands, marked as made before the rewind. */
  | { kind: 'roll'; roll: RollRecord; rewound?: true }
  /** An attack as a player may know it (issue 1124): their version, worked out on the server. The DM's is the roll. */
  | { kind: 'attack'; attack: PlayerAttack; rewound?: true }
  /** A template's saves as a player may know them (issue 1129). */
  | { kind: 'save'; save: PlayerSave; rewound?: true }
  /** A trigger's chain as a player may know it (issue 1500): a public one only. */
  | { kind: 'trigger'; trigger: PlayerTrigger; rewound?: true }
  | { kind: 'clear'; at: string; actorId: string | null }
  /** A focus note its setter also posted (slice 13, decision 23). */
  | { kind: 'note'; note: string; at: string; actorId: string | null; handle?: string | null; former?: boolean; /** A rule-check line (issue 1131): the DM's alone. */ rule?: true; /** A table announcement (issue 1491): no "pointed" label. */ announce?: true };

/** `cursor` is opaque: a sealed token the server alone can read, handed back as it came. */
/** `rules`: the table's rules settings (issue 1125), sent to the DM alone. */
/** `rulings`: the DM's rulings in force (issue 1136), the DM's whole, a player's as `PlayerRuling`. */
export interface LogPage { entries: LogEntry[]; more: boolean; cursor: string | null; asks?: Array<RollAsk | PlayerAsk>; rules?: TableRules; rulings?: Array<Ruling | PlayerRuling>; handouts?: Handouts; decks?: Decks }

/** The rulings a page carries: the DM's whole when each reads so, else as a player reads them. */
export function parsePageRulings(v: unknown): Array<Ruling | PlayerRuling> {
  if (!Array.isArray(v)) return [];
  const whole = normaliseRulings(v);
  return whole.length === v.length ? whole : parsePlayerRulings(v);
}

/** The stored bag, or the base bag when none is stored yet or the call failed. */
export async function fetchBag(fetchImpl: typeof fetch = fetch): Promise<Bag> {
  const { status, body } = await requestJson('/api/me/dice', { method: 'GET' }, fetchImpl);
  return (status === 200 && parseBag(body)) || defaultBag();
}

export async function saveBag(bag: Bag, fetchImpl: typeof fetch = fetch): Promise<boolean> {
  const { status } = await requestJson('/api/me/dice', { method: 'PUT', body: JSON.stringify(bag) }, fetchImpl);
  return status === 200;
}

/** The most recent page of the log before `before` (a cursor from an earlier page), oldest first. */
export async function fetchLog(campaignId: string, before: string | null = null, fetchImpl: typeof fetch = fetch): Promise<LogPage> {
  const q = before === null ? '' : `?before=${encodeURIComponent(before)}`;
  const { status, body } = await requestJson(`/api/tables/${campaignId}/log${q}`, { method: 'GET' }, fetchImpl);
  if (status !== 200 || typeof body !== 'object' || body === null) return { entries: [], more: false, cursor: null };
  const page = body as Partial<LogPage>;
  return { entries: Array.isArray(page.entries) ? page.entries : [], more: page.more === true, cursor: typeof page.cursor === 'string' ? page.cursor : null, ...(Array.isArray(page.asks) ? { asks: page.asks } : {}), ...(page.rules && parseRules(page.rules) ? { rules: parseRules(page.rules)! } : {}), ...(Array.isArray(page.rulings) ? { rulings: parsePageRulings(page.rulings) } : {}), ...(Array.isArray(page.handouts) ? { handouts: parsePageHandouts(page.handouts) } : {}), ...(Array.isArray(page.decks) ? { decks: parsePageDecks(page.decks) } : {}) };
}

/** The saved file, as text, or null when it is not this person's to save. */
export async function fetchLogText(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<string | null> {
  try {
    const res = await authFetch(`/api/tables/${campaignId}/log.txt`, {}, fetchImpl);
    return res.ok ? await res.text() : null;
  } catch {
    return null;
  }
}
